import {createContext, type ReactNode, type RefObject, use, useMemo} from 'react'

import type {PanelArtifact} from './ArtifactPanelContent'
import type {ArtifactTabItem} from './use-artifacts-panel-state'
import {useArtifactsPanelState as useArtifactsPanelStateValue} from './use-artifacts-panel-state'

type ArtifactsPanelState = {
  activeArtifactId: string | null
  activeArtifact: PanelArtifact | null
  editingArtifact: ReturnType<typeof useArtifactsPanelStateValue>['editingArtifact']
  tabs: ArtifactTabItem[]
}

type ArtifactsPanelActions = Pick<
  ReturnType<typeof useArtifactsPanelStateValue>,
  | 'selectArtifact'
  | 'activate'
  | 'updateArtifact'
  | 'reconcileArtifacts'
  | 'editArtifact'
  | 'closeEditArtifact'
  | 'closePanel'
  | 'setClosePanelCallback'
  | 'promoteOverflow'
  | 'closeTab'
  | 'removeArtifact'
>

const ArtifactsPanelStateContext = createContext<ArtifactsPanelState | null>(null)
const ArtifactsPanelActionsContext = createContext<ArtifactsPanelActions | null>(null)
const ArtifactsPanelReturnFocusRefContext = createContext<RefObject<HTMLElement | null> | null>(null)

export function ArtifactsPanelProvider({children}: {children: ReactNode}) {
  const {
    activeArtifactId,
    activeArtifact,
    editingArtifact,
    tabs,
    lastRowTriggerRef,
    selectArtifact,
    activate,
    updateArtifact,
    reconcileArtifacts,
    editArtifact,
    closeEditArtifact,
    closePanel,
    setClosePanelCallback,
    promoteOverflow,
    closeTab,
    removeArtifact,
  } = useArtifactsPanelStateValue()
  const state = useMemo(
    () => ({activeArtifactId, activeArtifact, editingArtifact, tabs}),
    [activeArtifactId, activeArtifact, editingArtifact, tabs],
  )
  const actions = useMemo(
    () => ({
      selectArtifact,
      activate,
      updateArtifact,
      reconcileArtifacts,
      editArtifact,
      closeEditArtifact,
      closePanel,
      setClosePanelCallback,
      promoteOverflow,
      closeTab,
      removeArtifact,
    }),
    [
      selectArtifact,
      activate,
      updateArtifact,
      reconcileArtifacts,
      editArtifact,
      closeEditArtifact,
      closePanel,
      setClosePanelCallback,
      promoteOverflow,
      closeTab,
      removeArtifact,
    ],
  )

  return (
    <ArtifactsPanelStateContext value={state}>
      <ArtifactsPanelActionsContext value={actions}>
        <ArtifactsPanelReturnFocusRefContext value={lastRowTriggerRef}>{children}</ArtifactsPanelReturnFocusRefContext>
      </ArtifactsPanelActionsContext>
    </ArtifactsPanelStateContext>
  )
}

export function useArtifactsPanelState() {
  const context = use(ArtifactsPanelStateContext)
  if (!context) {
    throw new Error('useArtifactsPanelState must be used within an ArtifactsPanelProvider')
  }
  return context
}

export function useArtifactsPanelActions() {
  const context = use(ArtifactsPanelActionsContext)
  if (!context) {
    throw new Error('useArtifactsPanelActions must be used within an ArtifactsPanelProvider')
  }
  return context
}

export function useArtifactsPanelReturnFocusRef() {
  const context = use(ArtifactsPanelReturnFocusRefContext)
  if (!context) {
    throw new Error('useArtifactsPanelReturnFocusRef must be used within an ArtifactsPanelProvider')
  }
  return context
}
